// noinspection JSUnusedGlobalSymbols

import {HttpClient, HttpContext, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from '@enviroment/environment';
import {Settlement} from '@core/models/Settlement';
import {UserType} from '@core/models/UserType';
import {BusinessGroup} from '@core/models/BusinessGroup';
import {Branch} from '@core/models/Branch';
import {DefaultResponse} from '@core/models/Http/DefaultResponse';
import {FederalEntity} from '@core/models/FederalEntity';
import {Allergy} from '@core/models/Allergy';
import {Patology} from '@core/models/Patology';
import {Molecule} from "@core/models/Molescule";
import {Medicament} from "@core/models/Medicament";
import {PatientDetails} from "@core/models/PatientDetails";
import {GEMP} from "@core/models/GEMP";
import {HealthEvent} from "@core/models/HealthEvent";
import {REGEX} from "@shared/utils/regex.constants";
import { DEDUP_REQUEST } from '@core/interceptor/request-dedup.interceptor';
import {GeneroVisible} from "@core/models/GeneroVisible";

@Injectable({
  providedIn: 'root',
})
export class CatalogsService {
  constructor(private http: HttpClient) {
  }

  getSettlementByPostalCode(name: string) {
    const isPostalCode = REGEX.postCode.test(name);

    if (isPostalCode) {
      return this.http.get<DefaultResponse<Settlement[]>>(
        `${environment.backend_url}Catalogo/AsentamientoByNames?CodigoPostal=${name}`
      );
    } else {
      return this.http.get<DefaultResponse<Settlement[]>>(
        `${environment.backend_url}Catalogo/AsentamientoByNames?NombreAsentamiento=${name}`
      );
    }
  }

  getUserTypes() {
    return this.http.get<DefaultResponse<UserType[]>>(
      `${environment.backend_url}TipoUsuario`
    );
  }


  getFederalEntitys() {
    return this.http.get<DefaultResponse<FederalEntity[]>>(
      `${environment.backend_url}PacientesCatalogo/entidades-federativas`
    );
  }

  getBusinessGroups() {
    return this.http.get<DefaultResponse<BusinessGroup[]>>(
      `${environment.backend_url}CatGrupoEmpresarial/GetGrupoEmpresarial`
    );
  }

  getBranchesByIdGEMP(idGEMP?: string) {
    let params = new HttpParams();

    if (idGEMP) {
      params = params.set('IdGEMP', idGEMP);
    }

    return this.http.get<DefaultResponse<Branch[]>>(
      `${environment.backend_url}Sucursales/GetSucursales`,
      { params }
    );
  }

  getBranchesByAuthenticatedUser() {
    return this.http.get<DefaultResponse<Branch[]>>(
      `${environment.backend_url}Consulta/SucursalesPorUsuario`
    );
  }

  private readonly jsonHeaders = { headers: { 'Content-Type': 'application/json' } };

  // ✅ Alergias
  getAllergiesByName(name: string) {
    const url = `${environment.backend_url}VIDAL_Allergy/AllergyByName`;
    // Enviar objeto { name } en el body, NO JSON.stringify(name)
    return this.http.post<DefaultResponse<Allergy[]>>(
      url,
      { name: (name ?? '').trim() },
      {
        ...this.jsonHeaders,
        context: new HttpContext().set(DEDUP_REQUEST, true)
      }
    );
  }

  // ✅ Moleculas
  getMolesculesByName(name: string) {
    const url = `${environment.backend_url}VIDAL_Molecule/MoleculeByName`;
    return this.http.post<DefaultResponse<Molecule[]>>(
      url,
      { name: (name ?? '').trim() },
      {
        ...this.jsonHeaders,
        context: new HttpContext().set(DEDUP_REQUEST, true)
      }
    );
  }

  getPatologiessByName(nombre: string, edad: number, genero: string) {
    const url = `${environment.backend_url}VIDAL_CIM10/CIM10ByName`;

    // El body debe incluir los 3 parametros como un objeto JSON
    const body = {
      nombre: nombre,
      edad: edad,
      genero: genero
    };

    return this.http.post<DefaultResponse<Patology[]>>(url, body, {
      headers: { 'Content-Type': 'application/json' },
      context: new HttpContext().set(DEDUP_REQUEST, true)
    });
  }


  getMedicamentsByName(name: string) {
    return this.http.post<DefaultResponse<Medicament[]>>(
      `${environment.backend_url}Consulta/MedicamentoByName?name=${name}`,
      {},
      { context: new HttpContext().set(DEDUP_REQUEST, true) }
    );
  }

  getPatientsByName(name: string) {
    const params = new HttpParams().set('NombreBusqueda', name);
    return this.http.get<DefaultResponse<PatientDetails[]>>(
      `${environment.backend_url}Pacientes`,
      {
        params,
        context: new HttpContext().set(DEDUP_REQUEST, true)
      }
    );
  }

  getGempbyIdGemp(idGemp: string | null) {
    return this.http.get<DefaultResponse<GEMP>>(
      `${environment.backend_url}CatGrupoEmpresarial/GetGrupoEmpresarial`,
      {
        params: { id: idGemp ?? '' }
      }
    );
  }

  getRecetaByIdReceta(idReceta: string, idPaciente: string | null, pin: string | null = null) {
    const data = {
      idReceta,
      idPaciente: idPaciente ?? null,
      pin: pin ?? null
    };

    return this.http.post(
      `${environment.backend_url}Recetas/GetRecetaByIdReceta`,
      data,
      {
        responseType: 'text'
      }
    );
  }

  getconsultaRecetaByIdReceta(idReceta: string) {
    return this.http.get(`${environment.backend_url}Recetas/ConsultarReceta/${idReceta}`);
  }

  getUpdateRecetaByIdReceta(idReceta: string, idPaciente: string) {
    return this.http.post(`${environment.backend_url}Recetas/GetRecetaUpdateByIdReceta`, {
      idReceta,
      idPaciente
    });
  }


  getEventosDeSalud() {
    return this.http.get<DefaultResponse<HealthEvent[]>>(
      `${environment.backend_url}CatEventosDeSalud/CateventosSalud`)
  }

  timbrarReceta(idReceta: string) {
    const url = `${environment.backend_url}Recetas/TimbrarReceta`;
    return this.http.post(url, { idReceta });
  }

  getGenerosVisibles() {
    return this.http.get<DefaultResponse<GeneroVisible[]>>(
      `${environment.backend_url}Pacientes/generos`
    );
  }

}
